import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSlidersH, faSpinner } from '@fortawesome/free-solid-svg-icons';
import { checklistQueriesToScreenerTokens } from '~/utilities/checklistQueryParse';
import { captureException } from '~/utilities/sentry.utility';
import MatchingCompanyRow, { MatchingCompany } from './MatchingCompanyRow';

const PAGE_SIZE = 30;

interface MatchingCompaniesListProps {
  initialCompanies: MatchingCompany[];
  total: number;
  screenerUrl: string[];
  /** Reset signal: change this value to force the list back to page 1 + use initialCompanies (e.g. when criteria mutate). */
  resetKey: number;
  isInitialLoading: boolean;
}

const MatchingCompaniesList: React.FC<MatchingCompaniesListProps> = ({
  initialCompanies,
  total,
  screenerUrl,
  resetKey,
  isInitialLoading,
}) => {
  const [companies, setCompanies] = useState<MatchingCompany[]>(initialCompanies);
  const [loadedPage, setLoadedPage] = useState(1);
  const [isPageLoading, setIsPageLoading] = useState(false);
  const sentinelRef = useRef<HTMLDivElement | null>(null);
  // Track latest page in a ref so the IntersectionObserver callback always sees the current value
  // without re-creating the observer on every page change.
  const loadedPageRef = useRef(1);
  const isLoadingRef = useRef(false);

  useEffect(() => {
    setCompanies(initialCompanies);
    setLoadedPage(1);
    loadedPageRef.current = 1;
    setIsPageLoading(false);
    isLoadingRef.current = false;
  }, [resetKey, initialCompanies]);

  const fetchPage = useCallback(
    async (page: number) => {
      const tokens = checklistQueriesToScreenerTokens(screenerUrl);
      if (tokens.length === 0) return;
      if (isLoadingRef.current) return;
      isLoadingRef.current = true;
      setIsPageLoading(true);
      try {
        const response = await axios.post('/api/company-screener', {
          data: tokens,
          limit: PAGE_SIZE,
          offset: (page - 1) * PAGE_SIZE,
          sort_by: 'market_cap',
          sort_direction: 'desc',
        });
        const next: MatchingCompany[] = response.data?.data ?? [];
        setCompanies((prev) => {
          const seen = new Set(prev.map((c) => `${c.company_id}-${c.ticker_id}`));
          const deduped = next.filter((c) => !seen.has(`${c.company_id}-${c.ticker_id}`));
          return [...prev, ...deduped];
        });
        setLoadedPage(page);
        loadedPageRef.current = page;
      } catch (error: any) {
        console.error('Error fetching matching companies:', error?.message);
        captureException(error);
      } finally {
        isLoadingRef.current = false;
        setIsPageLoading(false);
      }
    },
    [screenerUrl]
  );

  const hasMore = companies.length < total;

  useEffect(() => {
    if (!hasMore) return;
    if (isInitialLoading) return;
    const target = sentinelRef.current;
    if (!target) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (!entry.isIntersecting) return;
        if (isLoadingRef.current) return;
        fetchPage(loadedPageRef.current + 1);
      },
      { rootMargin: '200px 0px' }
    );

    observer.observe(target);
    return () => observer.disconnect();
  }, [hasMore, isInitialLoading, fetchPage]);

  return (
    <section className="mx-auto mt-6 w-full max-w-3xl md:mt-8">
      <div className="mb-3 md:mb-4">
        <div className="flex justify-center">
          <h2 className="text-center text-sm font-bold tracking-tight text-slate-900 md:text-base">
            {isInitialLoading ? (
              <span className="inline-block h-5 w-40 animate-pulse rounded bg-slate-200 align-middle md:h-6 md:w-56" />
            ) : (
              <>
                <span className="font-semibold text-slate-600">Found </span>
                <span className="mx-0.5 inline-flex h-6 min-w-6 items-center justify-center rounded-full bg-black px-2 align-middle text-xs font-bold tabular-nums text-white md:h-7 md:min-w-7 md:px-2.5 md:text-sm">
                  {total}
                </span>
                <span className="font-semibold text-slate-600">
                  {total === 1 ? " company" : " companies"}
                </span>
              </>
            )}
          </h2>
        </div>
      </div>

      {isInitialLoading ? (
        <ul className="divide-y divide-slate-200/80" aria-hidden>
          {Array.from({ length: 6 }).map((_, idx) => (
            <li key={idx} className="flex w-full items-center gap-3 px-2 py-3 sm:gap-4 sm:py-3.5 md:px-4 lg:px-6">
              <div className="h-8 w-8 shrink-0 animate-pulse rounded-full bg-slate-100 sm:h-9 sm:w-9" />
              <div className="flex min-w-0 flex-1 flex-col gap-1.5">
                <div className="h-3 w-24 animate-pulse rounded bg-slate-100 sm:h-3.5 sm:w-32" />
                <div className="h-2.5 w-40 animate-pulse rounded bg-slate-100 sm:h-3 sm:w-56" />
              </div>
              <div className="flex shrink-0 flex-col items-end gap-1.5">
                <div className="h-2 w-12 animate-pulse rounded bg-slate-100 sm:h-2.5 sm:w-16" />
                <div className="h-3 w-16 animate-pulse rounded bg-slate-100 sm:h-4 sm:w-20" />
              </div>
            </li>
          ))}
        </ul>
      ) : total === 0 ? (
        <div className="rounded-2xl border border-dashed border-slate-200 bg-slate-50/60 px-4 py-10 text-center">
          <div className="mx-auto flex h-10 w-10 items-center justify-center rounded-full bg-white text-slate-400 shadow-sm md:h-12 md:w-12">
            <FontAwesomeIcon icon={faSlidersH} className="text-sm md:text-base" aria-hidden />
          </div>
          <p className="mt-3 text-sm font-semibold text-slate-700 md:text-[15px]">
            No companies match these criteria
          </p>
          <p className="mt-1 text-xs text-slate-500 md:text-sm">
            Try removing or relaxing one of your criteria.
          </p>
        </div>
      ) : (
        <>
          <ul className="divide-y divide-slate-200/80">
            {companies.map((c) => (
              <MatchingCompanyRow key={`${c.company_id}-${c.ticker_id}`} company={c} />
            ))}
          </ul>

          {hasMore && (
            <div
              ref={sentinelRef}
              className="mt-4 flex items-center justify-center py-4 md:mt-6"
              aria-hidden={!isPageLoading}
            >
              {isPageLoading ? (
                <span
                  role="status"
                  aria-label="Loading more companies"
                  className="inline-flex items-center gap-2 text-xs font-medium text-slate-500 md:text-sm"
                >
                  <FontAwesomeIcon
                    icon={faSpinner}
                    className="h-4 w-4 animate-spin text-emerald-600"
                    aria-hidden
                  />
                  <span>Loading more…</span>
                </span>
              ) : (
                <span className="h-4 w-4" aria-hidden />
              )}
            </div>
          )}

          {!hasMore && companies.length > PAGE_SIZE && (
            <p className="mt-4 text-center text-[11px] font-medium text-slate-400 md:mt-6 md:text-xs">
              You&rsquo;ve reached the end &middot;{' '}
              <span className="tabular-nums text-slate-600">{companies.length}</span> of{' '}
              <span className="tabular-nums text-slate-600">{total}</span>
            </p>
          )}
        </>
      )}
    </section>
  );
};

export default MatchingCompaniesList;
